/**
 * The i18n engine both web surfaces run on.
 *
 * WHY THIS IS SHARED AND THE CATALOGUES ARE NOT. The machinery — locale
 * detection, per-key fallback, placeholder substitution, `Intl` formatters — is
 * identical for every surface and is the part that is easy to get subtly wrong.
 * The STRINGS are not: App speaks to a professional mid-onboarding and Book to
 * a client mid-booking, they share almost no copy, and a single merged
 * catalogue would be the one file every screen on both surfaces has to touch.
 * So each app owns `src/i18n/messages/` and imports the engine from here.
 *
 * WHY NO LIBRARY. Carried verbatim from the Book catalogue this was lifted
 * from, because the reasoning did not change by moving: every string either is
 * a literal or is one of four formatting problems — number, currency,
 * date/time, plural — and all four are native `Intl`. i18next unpacks at
 * ~500 kB and react-i18next at ~1.5 MB, on surfaces reached by a phone with a
 * QR code and whatever signal it has. Lingui would be defensible at ~22 kB; a
 * typed record and `Intl` is smaller still and gives compile-time key safety,
 * which none of them do by default.
 *
 * This remains a judgement call and it remains reversible: `t()` is the only
 * thing views touch. If plural rules or message syntax start getting
 * complicated, that is the signal to take Lingui.
 */

import { safeStorage } from "../lib/safeStorage";

/** A catalogue for one locale. Only the source locale is required to be total. */
export type Catalogue<K extends string> = Partial<Record<K, string>>;

export interface I18nConfig<L extends string, K extends string> {
  /** The locale every other one falls back to, per key. */
  readonly defaultLocale: L;
  /** The default locale's catalogue, COMPLETE — this is what fallback resolves to. */
  readonly source: Readonly<Record<K, string>>;
  /** Every locale including the default. Non-default entries may be partial. */
  readonly catalogues: Readonly<Record<L, Catalogue<K>>>;
  /**
   * Where a chosen locale is remembered. Per-surface, because App and Book are
   * separate ORIGINS — `localStorage` does not cross them, so one shared key
   * would imply a carry-over that cannot happen. Each app passes its own.
   */
  readonly storageKey: string;
}

export interface I18n<L extends string, K extends string> {
  readonly LOCALES: readonly L[];
  isLocale(value: unknown): value is L;
  detectLocale(): L;
  setLocale(locale: L): void;
  translate(locale: L, key: K, values?: Record<string, string | number>): string;
  formatters(locale: L): Formatters;
}

export interface Formatters {
  money(cents: number): string;
  duration(minutes: number): string;
  date(iso: string, timeZone: string): string;
  time(iso: string, timeZone: string): string;
}

/**
 * An ISO instant a surface can actually render.
 *
 * On Book the selection lives in the QUERY STRING, so `?time=` is one hand-edit
 * or one mangled email link away from being anything at all. `new Date("junk")`
 * is an Invalid Date, and `Intl.DateTimeFormat().format()` THROWS `RangeError`
 * on one — during render, which takes out the whole route rather than the field.
 */
export function isValidInstant(iso: string | null | undefined): iso is string {
  return typeof iso === "string" && !Number.isNaN(new Date(iso).getTime());
}

/**
 * Format, or say nothing — but never throw.
 *
 * Guards upstream should stop an unparseable instant before it reaches a
 * formatter, and they do. This is the backstop for the case they miss, because
 * the failure mode without it is a blank error page rather than a missing time.
 *
 * An em dash rather than an empty string: something visibly absent is a bug
 * someone reports, and a silent gap is a bug someone lives with. The timeZone
 * is guarded by the same try — a bad IANA name throws from the CONSTRUCTOR, not
 * from format, so checking the date alone would not have covered it.
 */
function safeFormat(iso: string, build: () => Intl.DateTimeFormat): string {
  try {
    return build().format(new Date(iso));
  } catch {
    return "—";
  }
}

/**
 * Hour words for the duration formatter.
 *
 * `Intl.DurationFormat` would own this, but Safari shipped it late enough that
 * a booking surface aimed at phones cannot rely on it yet. A locale absent from
 * this table falls back to the default locale's words rather than rendering a
 * number with no unit.
 */
const HOUR_WORDS: Record<string, { one: string; other: string; minute: string }> = {
  en: { one: "hr", other: "hr", minute: "min" },
  es: { one: "hora", other: "horas", minute: "min" },
};

export function createI18n<L extends string, K extends string>(
  config: I18nConfig<L, K>
): I18n<L, K> {
  const LOCALES = Object.keys(config.catalogues) as L[];

  function isLocale(value: unknown): value is L {
    return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
  }

  /**
   * Which language to render in.
   *
   * Order: an explicit choice the visitor made, then what their browser asks
   * for, then the default. `navigator.languages` is checked in order and
   * matched on the PRIMARY subtag, so `es-MX` and `es-419` both find `es` —
   * matching the full tag would silently drop most Spanish speakers.
   */
  function detectLocale(): L {
    // Private browsing and some embedded webviews throw on storage access
    // rather than returning null; safeStorage reads that as null. A language
    // preference is not worth a blank page, so fall through to the browser's.
    const stored = safeStorage.local.get(config.storageKey);
    if (isLocale(stored)) return stored;

    const requested = globalThis.navigator?.languages ?? [];
    for (const tag of requested) {
      const primary = tag.split("-")[0]?.toLowerCase();
      if (isLocale(primary)) return primary;
    }
    return config.defaultLocale;
  }

  function setLocale(locale: L): void {
    // Same as above — when storage fails the choice just does not persist.
    safeStorage.local.set(config.storageKey, locale);
  }

  /**
   * Look up a message and substitute `{name}` placeholders.
   *
   * Falls back to the source locale per KEY rather than per locale: a partially
   * translated catalogue shows Spanish where it has it and English where it
   * does not, which is better than an all-or-nothing switch. The untranslated
   * count is asserted in scripts/validate-quality-contracts.js so "partial"
   * cannot quietly become "abandoned".
   */
  function translate(
    locale: L,
    key: K,
    values?: Record<string, string | number>
  ): string {
    const catalogue = config.catalogues[locale];
    const template = catalogue[key] ?? config.source[key];

    if (!values) return template;

    // Only substitutes placeholders the caller supplied. An unknown `{token}`
    // is left as-is rather than blanked, so a missing value is visible in
    // review instead of rendering a sentence with a hole in it.
    return template.replace(/\{(\w+)\}/g, (match, name: string) =>
      name in values ? String(values[name]) : match
    );
  }

  /** Locale-correct formatters. A booking flow is mostly these. */
  function formatters(locale: L): Formatters {
    const intlLocale = locale === "es" ? "es-US" : locale;
    const words = HOUR_WORDS[locale] ?? HOUR_WORDS[config.defaultLocale] ?? HOUR_WORDS.en!;

    return {
      /**
       * Cents to currency. The API exposes cents with no currency code; USD
       * until it says otherwise.
       */
      money: (cents: number) =>
        new Intl.NumberFormat(intlLocale, {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(cents / 100),

      duration: (minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        const plural = new Intl.PluralRules(intlLocale);
        const hourWord = plural.select(hours) === "one" ? words.one : words.other;
        if (hours === 0) return `${rest} ${words.minute}`;
        if (rest === 0) return `${hours} ${hourWord}`;
        return `${hours} ${hourWord} ${rest} ${words.minute}`;
      },

      /**
       * A date in the PROFESSIONAL's timezone — the appointment happens there,
       * not where the client is.
       */
      date: (iso: string, timeZone: string) =>
        safeFormat(iso, () =>
          new Intl.DateTimeFormat(intlLocale, {
            weekday: "long",
            month: "long",
            day: "numeric",
            timeZone,
          })
        ),

      time: (iso: string, timeZone: string) =>
        safeFormat(iso, () =>
          new Intl.DateTimeFormat(intlLocale, { hour: "numeric", minute: "2-digit", timeZone })
        ),
    };
  }

  return { LOCALES, isLocale, detectLocale, setLocale, translate, formatters };
}
